import React from "react";
import { useNavigate } from "react-router-dom";
import { STAGES, SESSIONS, WEEKS, isIntakeComplete } from "@/data/bridgeData";
import { useEntries } from "@/context/EntriesContext";
import { Check, Lock } from "lucide-react";

export default function JourneyMap() {
  const navigate = useNavigate();
  const { getEntry, loading } = useEntries();
  const currentWeek = Number(getEntry("meta:currentWeek", 1)) || 1;
  const locked = !loading && !isIntakeComplete(getEntry);

  return (
    <div className="rounded-sm border border-stone-200 bg-white p-8 lg:p-10">
      <div className="label-eyebrow mb-6">The 16-week journey</div>

      {/* Stage bands */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
        {STAGES.map((s, i) => {
          const active = WEEKS.find((w) => w.week === currentWeek)?.stage === s.id;
          return (
            <div key={s.id} className={`rounded-sm border p-4 transition-all duration-300 ${active ? "border-brand-primary bg-brand-parchment/40" : "border-stone-200"}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                <span className="text-xs tracking-[0.18em] uppercase text-stone-500">0{i + 1}</span>
              </div>
              <div className="font-serif text-xl text-stone-900">{s.name}</div>
              <div className="text-xs text-stone-400 mt-0.5">{s.weeks}</div>
              <p className="text-sm text-stone-600 mt-2 leading-snug">{s.goal}</p>
            </div>
          );
        })}
      </div>

      {/* Session nodes along the bridge */}
      <div className="relative pt-4">
        <div className="absolute left-0 right-0 top-9 h-px bg-stone-200" />
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-y-8">
          {SESSIONS.map((s) => {
            const reached = currentWeek >= s.week;
            let nodeClass = "bg-white border-stone-300 text-stone-400";
            if (locked) nodeClass = "bg-white border-stone-200 text-stone-300";
            else if (reached) nodeClass = "bg-brand-primary border-brand-primary text-white";
            let nodeContent = s.id;
            if (locked) nodeContent = <Lock size={14} />;
            else if (reached) nodeContent = <Check size={16} />;
            return (
              <button
                key={s.id}
                data-testid={`journey-session-${s.id}`}
                onClick={() => navigate(locked ? "/intake" : `/sessions/${s.id}`)}
                className="relative flex flex-col items-center group"
              >
                <span className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border text-sm transition-all duration-300 group-hover:-translate-y-1 ${nodeClass}`}>
                  {nodeContent}
                </span>
                <span className="mt-3 text-xs text-stone-400">Week {s.week}</span>
                <span className="mt-0.5 text-center text-sm text-stone-700 leading-tight px-1">Session {s.id}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
